import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BrandMark } from "../components/Brand";
import { api, formatCurrency, formatDate, formatApiError, openAuthedPdf } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { STATUS_STYLES } from "../components/ops/opsUtils";
import ImpersonationBanner from "../components/ImpersonationBanner";
import SettlementCalculator from "../components/SettlementCalculator";
import { getAttorney } from "../lib/attorneyAuth";
import {
  ArrowLeft, Receipt, FileSignature, Clock, Loader2, ExternalLink, Activity,
  FileText, AlertCircle, Download, ArrowRight, CheckCircle2, PlusCircle, X, DollarSign,
  Calculator, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

export default function AttorneyLoanDetail() {
  const { loan_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showSettlement, setShowSettlement] = useState(false);
  const [showFunding, setShowFunding] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  useSEO(data ? `${data.loan.client_name} · Case` : "Case", "Loan detail in the Instabridge firm portal.");

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [loan_id]);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/portal/loans/${loan_id}`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/attorney-login", { replace: true });
      else toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  const generatePayoff = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/portal/loans/${loan_id}/quick-payoff`, { valid_days: 10 });
      toast.success(`Payoff ${data.statement_number} generated`);
      await openAuthedPdf(`/portal/payoffs/${data.payoff_id}/pdf`, `${data.statement_number || data.payoff_id}.pdf`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  const submitSettlement = async (form) => {
    try {
      const { data: res } = await api.post(`/portal/loans/${loan_id}/settlement-reached`, form);
      toast.success("Settlement reported — Instabridge has been notified.");
      setShowSettlement(false);
      if (res?.payoff?.payoff_id) {
        await openAuthedPdf(`/portal/payoffs/${res.payoff.payoff_id}/pdf`, `${res.payoff.statement_number || res.payoff.payoff_id}.pdf`);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitFundingRequest = async (form) => {
    try {
      await api.post(`/portal/loans/${loan_id}/funding-request`, form);
      toast.success("Request submitted — Instabridge will respond within 1 business day.");
      setShowFunding(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <Splash />;
  if (!data) return null;

  const l = data.loan;
  const status = STATUS_STYLES[l.current_status] || { bg: "#F3F4F6", text: "#374151", label: l.current_status };
  // Per user request: only hide actions when the case is irrelevant (Declined).
  // For Settled/Closed loans we still allow regenerating historical snapshots —
  // the button label changes so users understand the context.
  const isDeclined = l.current_status === "Declined";
  const isFinalized = ["Settled", "Closed"].includes(l.current_status);
  const showSettlementBtn = !isDeclined && !isFinalized && l.current_status !== "PendingSettlement";
  const showFundingBtn = !isDeclined && !isFinalized;
  // Settlement calculator: useful for every status EXCEPT Declined.
  const showCalculator = !isDeclined;
  const clientKey = l.client_group_id || (l.client_name || "").trim().toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="attorney-loan-detail">
      <ImpersonationBanner impersonation={(getAttorney() || {}).impersonation} firm={(getAttorney() || {}).firm} />
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
        <div className="mx-auto max-w-[1300px] px-5 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/attorney/portal"><BrandMark /></Link>
          <Link to="/attorney/portal" className="text-xs text-[#6B7280] hover:text-[#0A2540] inline-flex items-center gap-1" data-testid="back-portal"><ArrowLeft className="w-3.5 h-3.5" /> Portal</Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1300px] px-5 lg:px-8 py-7 space-y-6">
        {/* Header card */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 lg:p-7">
          <div className="flex items-start justify-between flex-wrap gap-5">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-2">
                <FileSignature className="w-3.5 h-3.5" /> Case · {l.loan_id}
              </div>
              <h1 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-semibold">{l.client_name}</h1>
              <div className="flex items-center gap-3 mt-2 text-sm text-[#6B7280]">
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: status.bg, color: status.text }}>{status.label}</span>
                <span>{l.case_type || "—"}</span>
                {l.case_state && <span>· {l.case_state}</span>}
                <span>· funded {formatDate(l.funding_date)}</span>
              </div>
            </div>
            {!isDeclined && (
              <div className="flex flex-wrap gap-2">
                <button onClick={generatePayoff} disabled={generating} data-testid="loan-quick-payoff"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0A2540] hover:bg-[#001A3D] text-white text-sm font-medium transition-colors disabled:opacity-60">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                  {isFinalized ? "Regenerate payoff snapshot" : "Generate payoff"}
                </button>
                {showSettlementBtn && (
                  <button onClick={() => setShowSettlement(true)} data-testid="loan-settlement-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#047857] hover:bg-[#065F46] text-white text-sm font-medium transition-colors">
                    <CheckCircle2 className="w-4 h-4" /> Mark settlement reached
                  </button>
                )}
                {showFundingBtn && (
                  <button onClick={() => setShowFunding(true)} data-testid="loan-funding-request-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white border border-[#E5E7EB] hover:border-[#0066FF] hover:text-[#0066FF] text-[#0A2540] text-sm font-medium transition-colors">
                    <PlusCircle className="w-4 h-4" /> Request additional funding
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6 pt-6 border-t border-[#F3F4F6]">
            <Stat label="Principal" value={formatCurrency(l.principal)} />
            <Stat label="Current payoff" value={formatCurrency(l.current_payoff)} accent />
            <Stat label="Accrued interest" value={formatCurrency(l.accrued_interest)} />
            <Stat label="Months outstanding" value={l.months_outstanding != null ? `${Number(l.months_outstanding).toFixed(1)} mo` : "—"} />
          </div>
        </div>

        {/* Settlement calculator (collapsible) — available for every non-declined case */}
        {showCalculator && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid="loan-calc-section">
            <button onClick={() => setCalcOpen((o) => !o)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#F9FAFB] text-left" data-testid="loan-calc-toggle">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0066FF] to-[#001A3D] text-white flex items-center justify-center">
                  <Calculator className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280]">Settlement distribution calculator</div>
                  <div className="text-sm font-medium text-[#0A2540]">Model attorney fee, payoff, and estimated client net for this case.</div>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-[#6B7280] transition-transform ${calcOpen ? "rotate-180" : ""}`} />
            </button>
            {calcOpen && (
              <div className="px-5 pb-6 pt-2 border-t border-[#F3F4F6]">
                <SettlementCalculator
                  clientKey={clientKey}
                  clientName={l.client_name}
                  instabridgePayoffLive={l.current_payoff}
                />
              </div>
            )}
          </div>
        )}

        {/* Two column layout */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Left: documents + payoff history */}
          <div className="lg:col-span-2 space-y-5">
            <Section title="Agreements" icon={FileSignature} count={data.contracts.length} testid="section-contracts">
              {data.contracts.length === 0 ? (
                <EmptyRow label="No agreements on file for this case yet." />
              ) : data.contracts.map((c) => (
                <DocRow key={c.contract_id}
                  title="Funding Agreement"
                  sub={`${c.status || "Draft"} · ${formatDate(c.created_at)}`}
                  apiPath={`/portal/contracts/${c.contract_id}/pdf`}
                  filename={`${c.contract_id}.pdf`}
                  testid={`contract-${c.contract_id}`}
                />
              ))}
            </Section>

            <Section title="Payoff statements" icon={Receipt} count={data.payoffs.length} testid="section-payoffs">
              {data.payoffs.length === 0 ? (
                <EmptyRow label="No payoff letters yet — click ‘Generate payoff’ above for an instant snapshot." />
              ) : data.payoffs.map((p) => (
                <DocRow key={p.payoff_id}
                  title={p.statement_number || p.payoff_id}
                  sub={`${formatCurrency(p.final_amount)} · valid through ${formatDate(p.valid_through)}`}
                  apiPath={`/portal/payoffs/${p.payoff_id}/pdf`}
                  filename={`${p.statement_number || p.payoff_id}.pdf`}
                  testid={`payoff-${p.payoff_id}`}
                />
              ))}
            </Section>

            <Section title="Additional funding requests" icon={PlusCircle} count={(data.funding_requests || []).length} testid="section-funding-requests">
              {(data.funding_requests || []).length === 0 ? (
                <EmptyRow label="No additional funding requests yet." />
              ) : (data.funding_requests || []).map((r) => {
                const tone = r.status === "Approved" ? { bg: "#DCFCE7", text: "#166534" }
                  : r.status === "Denied" ? { bg: "#FEE2E2", text: "#B91C1C" }
                  : { bg: "#FEF3C7", text: "#D97706" };
                return (
                  <div key={r.request_id} className="px-5 py-3" data-testid={`funding-req-${r.request_id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center flex-shrink-0">
                          <DollarSign className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-[#0A2540]">{formatCurrency(r.amount_requested)} requested</div>
                          <div className="text-xs text-[#6B7280]">{r.attorney_email} · {formatDate(r.created_at)}</div>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: tone.bg, color: tone.text }}>{r.status}</span>
                    </div>
                    <p className="text-xs text-[#374151] mt-2 leading-relaxed">{r.reason}</p>
                    {r.decision_notes && (
                      <p className="text-xs text-[#6B7280] mt-1.5 italic">Instabridge: {r.decision_notes}</p>
                    )}
                  </div>
                );
              })}
            </Section>
          </div>

          {/* Right: timeline */}
          <div className="space-y-5">
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-4">
                <Activity className="w-3.5 h-3.5" /> Activity
              </div>
              {data.timeline.length === 0 ? (
                <div className="text-sm text-[#6B7280]">No activity recorded.</div>
              ) : (
                <ol className="relative border-l border-[#E5E7EB] ml-1 space-y-4" data-testid="loan-timeline">
                  {data.timeline.slice(0, 25).map((ev, i) => (
                    <li key={i} className="ml-4" data-testid={`timeline-${i}`}>
                      <div className="absolute w-2 h-2 bg-[#0066FF] rounded-full -left-[5px] mt-1.5" />
                      <div className="text-xs text-[#0A2540] font-medium">{prettyKind(ev.kind)}</div>
                      <div className="text-[11px] text-[#6B7280] tabular-nums">{formatDate(ev.at)}{ev.by ? ` · ${ev.by}` : ""}</div>
                      {summarizePayload(ev) && <div className="text-xs text-[#374151] mt-0.5">{summarizePayload(ev)}</div>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSettlement && (
        <SettlementModal
          loan={l}
          onClose={() => setShowSettlement(false)}
          onSubmit={submitSettlement}
        />
      )}
      {showFunding && (
        <FundingRequestModal
          loan={l}
          onClose={() => setShowFunding(false)}
          onSubmit={submitFundingRequest}
        />
      )}
    </div>
  );
}

function SettlementModal({ loan, onClose, onSubmit }) {
  const [settledAmount, setSettledAmount] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        settled_amount: settledAmount ? Number(settledAmount) : null,
        expected_close_date: closeDate || null,
        notes,
      });
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0A2540]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="settlement-modal">
      <form onSubmit={submit} className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg p-6 sm:p-7">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#047857] mb-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Settlement reached
            </div>
            <h2 className="font-display text-2xl text-[#0A2540] font-semibold">{loan.client_name}</h2>
            <p className="text-xs text-[#6B7280] mt-1">Auto-generates a payoff letter and alerts Instabridge to confirm + collect.</p>
          </div>
          <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A2540]" data-testid="settlement-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 mt-5">
          <div>
            <label className="text-xs text-[#6B7280] font-medium">Gross settlement amount (optional)</label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input inputMode="decimal" value={settledAmount} onChange={(e) => setSettledAmount(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="85,000" className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]" data-testid="settlement-amount" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#6B7280] font-medium">Expected close / disbursement date</label>
            <input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]" data-testid="settlement-close-date" />
          </div>
          <div>
            <label className="text-xs text-[#6B7280] font-medium">Notes for Instabridge ops (optional)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Adjuster confirmed in writing. Disbursement check anticipated in 7–10 business days." className="w-full px-3 py-2 rounded-md border border-[#E5E7EB] text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]" data-testid="settlement-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md text-sm text-[#6B7280] hover:text-[#0A2540]">Cancel</button>
          <button type="submit" disabled={submitting} className="h-10 px-5 rounded-md bg-[#047857] hover:bg-[#065F46] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60" data-testid="settlement-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Mark settled
          </button>
        </div>
      </form>
    </div>
  );
}

function FundingRequestModal({ loan, onClose, onSubmit }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [medical, setMedical] = useState("");
  const [timeline, setTimeline] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        amount_requested: Number(amount),
        reason,
        medical_update: medical,
        timeline_update: timeline,
      });
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0A2540]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="funding-request-modal">
      <form onSubmit={submit} className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg p-6 sm:p-7">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#1D4ED8] mb-2">
              <PlusCircle className="w-3.5 h-3.5" /> Additional funding
            </div>
            <h2 className="font-display text-2xl text-[#0A2540] font-semibold">{loan.client_name}</h2>
            <p className="text-xs text-[#6B7280] mt-1">We typically respond within 1 business day with an approval, a counter, or follow-up questions.</p>
          </div>
          <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A2540]" data-testid="funding-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 mt-5">
          <div>
            <label className="text-xs text-[#6B7280] font-medium">Amount requested</label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input inputMode="decimal" required value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="2,500" className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]" data-testid="funding-amount" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#6B7280] font-medium">Reason for request</label>
            <textarea required rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., client needs to cover MRI scan that insurance denied; case moving toward mediation in 6 weeks." className="w-full px-3 py-2 rounded-md border border-[#E5E7EB] text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]" data-testid="funding-reason" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#6B7280] font-medium">Medical update (optional)</label>
              <input value={medical} onChange={(e) => setMedical(e.target.value)} placeholder="PT, MRI, surgery, etc." className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]" data-testid="funding-medical" />
            </div>
            <div>
              <label className="text-xs text-[#6B7280] font-medium">Timeline update (optional)</label>
              <input value={timeline} onChange={(e) => setTimeline(e.target.value)} placeholder="Mediation date, demand sent, etc." className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]" data-testid="funding-timeline" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md text-sm text-[#6B7280] hover:text-[#0A2540]">Cancel</button>
          <button type="submit" disabled={submitting || !amount || !reason.trim()} className="h-10 px-5 rounded-md bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60" data-testid="funding-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />} Submit request
          </button>
        </div>
      </form>
    </div>
  );
}

function prettyKind(k) {
  return ({
    status_change: "Status changed",
    payoff_generated: "Payoff generated",
    generated_via_settlement: "Payoff generated (settlement)",
    contract_created: "Agreement drafted",
    contract_signed: "Agreement signed",
    advance_added: "Additional advance",
    paydown_paydown: "Paydown recorded",
    paydown_reduction: "Reduction applied",
    settlement_reported: "Settlement reported",
    funding_requested: "Additional funding requested",
    funding_request_decided: "Funding request decided",
    settled: "Settlement recorded",
  })[k] || k.replace(/_/g, " ");
}

function summarizePayload(ev) {
  const p = ev.payload || {};
  if (ev.kind === "status_change") return `${p.from || "?"} → ${p.to || "?"}`;
  if (ev.kind === "payoff_generated") return p.amount != null ? `${formatCurrency(p.amount)}` : null;
  if (ev.kind === "settlement_reported") {
    const bits = [];
    if (p.settled_amount) bits.push(`${formatCurrency(p.settled_amount)}`);
    if (p.expected_close_date) bits.push(`close ${formatDate(p.expected_close_date)}`);
    return bits.join(" · ") || null;
  }
  if (ev.kind === "funding_requested") return `${formatCurrency(p.amount)} — ${(p.reason || "").slice(0, 80)}`;
  if (ev.kind === "funding_request_decided") return `${p.decision}${p.approved_amount ? ` · ${formatCurrency(p.approved_amount)}` : ""}`;
  if (ev.kind === "contract_created" || ev.kind === "contract_signed") return p.template_id || p.contract_id;
  return null;
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
      <div className={`tabular-nums font-display ${accent ? "text-[#0066FF] text-2xl font-semibold" : "text-[#0A2540] text-xl font-medium"}`}>{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children, testid }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-[#0A2540]"><Icon className="w-4 h-4 text-[#0066FF]" /> {title}</div>
        <span className="text-xs text-[#6B7280] tabular-nums">{count}</span>
      </div>
      <div className="divide-y divide-[#F3F4F6]">{children}</div>
    </div>
  );
}

function DocRow({ title, sub, apiPath, filename, testid }) {
  const [busy, setBusy] = useState(false);
  const open = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try { await openAuthedPdf(apiPath, filename); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={open} disabled={busy} className="w-full flex items-center justify-between px-5 py-3 hover:bg-[#F9FAFB] group disabled:opacity-60 text-left" data-testid={testid}>
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="w-4 h-4 text-[#0066FF] flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#0A2540] truncate">{title}</div>
          <div className="text-xs text-[#6B7280] truncate">{sub}</div>
        </div>
      </div>
      {busy ? <Loader2 className="w-3.5 h-3.5 text-[#9CA3AF] animate-spin" /> : <ExternalLink className="w-3.5 h-3.5 text-[#9CA3AF] group-hover:text-[#0066FF]" />}
    </button>
  );
}

function EmptyRow({ label }) {
  return <div className="px-5 py-6 text-center text-sm text-[#6B7280]">{label}</div>;
}

function Splash() {
  return <div className="min-h-screen flex items-center justify-center text-[#6B7280]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading case…</div>;
}
